/**
 * ClientActivityTracker — beacon de navegación de clientes (round 2026-07-25).
 *
 * Se monta una sola vez dentro de <Router> + <AuthProvider> (en AppRoutes).
 * En cada cambio de ruta de la SPA, si el usuario autenticado es un CLIENTE,
 * dispara un POST fire-and-forget a /actividad-clientes/navegacion para que el
 * historial de actividad registre qué páginas visita. No renderiza nada.
 *
 * Solo clientes: los admins tienen su propio audit log (admin_audit). Rutas
 * públicas/de auth no se registran. Ver docs/modulo-actividad-clientes.md.
 */

import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/roles.utils";
import { actividadClientesApi } from "../services/actividadClientes.api";

// Nombre amigable por ruta (para la columna "Detalle" del historial). Cubre
// las páginas que ve un cliente; el resto cae al primer segmento capitalizado.
const RUTA_LABELS: Record<string, string> = {
  "/home": "Inicio",
  "/calendario": "Calendario",
  "/finanzas-cliente": "Finanzas",
  "/finanzas-clientes": "Finanzas",
  "/estadisticas": "Estadísticas",
  "/documentos": "Documentos",
  "/contratos": "Documentos",
  "/tareas-cliente": "Tareas",
  "/mercado-cliente": "Precios y demanda",
  "/perfil": "Perfil",
};

function tituloDeRuta(pathname: string): string | undefined {
  if (RUTA_LABELS[pathname]) return RUTA_LABELS[pathname];
  const seg = pathname.split("/").filter(Boolean)[0];
  return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : undefined;
}

const ClientActivityTracker: React.FC = () => {
  const { user, token } = useAuth();
  const location = useLocation();
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token || !user) return;
    if (normalizeRole(user.rol) !== "cliente") return;

    const path = location.pathname;
    // Evita duplicados por re-render / React StrictMode (doble efecto en dev).
    if (lastPathRef.current === path) return;
    lastPathRef.current = path;

    // No registrar rutas públicas / de autenticación.
    if (
      path.startsWith("/login") ||
      path.startsWith("/recuperar-contrasena")
    ) {
      return;
    }

    actividadClientesApi.registrarNavegacion(path, tituloDeRuta(path));
  }, [location.pathname, user, token]);

  return null;
};

export default ClientActivityTracker;
